import { IDentalData } from '@dataTypes/chartingFeature.types'

/** Chart connector types (left = mesial overlay, right = distal overlay). */
export type BridgeConnectorType =
  | 'left_bridge'
  | 'right_bridge'
  | 'right_left_bridge'
  | 'left_abutment'
  | 'right_abutment'
  | 'right_left_abutment'

const CONNECTOR_TYPES = new Set<string>([
  'left_bridge',
  'right_bridge',
  'right_left_bridge',
  'left_abutment',
  'right_abutment',
  'right_left_abutment',
])

const isConnectorType = (type: string | null): type is BridgeConnectorType =>
  type !== null && CONNECTOR_TYPES.has(type)

/** Anatomical mesial/distal neighbors (toward / away from arch midline). */
export const getMesialDistalNeighbors = (
  toothNumber: number,
): { mesial: number | null; distal: number | null } => {
  if (toothNumber >= 1 && toothNumber <= 8) {
    return {
      mesial: toothNumber < 9 ? toothNumber + 1 : null,
      distal: toothNumber > 1 ? toothNumber - 1 : null,
    }
  }
  if (toothNumber >= 9 && toothNumber <= 16) {
    return {
      mesial: toothNumber > 8 ? toothNumber - 1 : null,
      distal: toothNumber < 16 ? toothNumber + 1 : null,
    }
  }
  if (toothNumber >= 17 && toothNumber <= 24) {
    return {
      mesial: toothNumber < 25 ? toothNumber + 1 : null,
      distal: toothNumber > 17 ? toothNumber - 1 : null,
    }
  }
  if (toothNumber >= 25 && toothNumber <= 32) {
    return {
      mesial: toothNumber > 24 ? toothNumber - 1 : null,
      distal: toothNumber < 32 ? toothNumber + 1 : null,
    }
  }
  return { mesial: null, distal: null }
}

const parseToothNumber = (value: string | number): number => {
  const n = parseInt(String(value).trim(), 10)
  return Number.isFinite(n) ? n : -1
}

const groupDentalDataByTooth = (
  dentalData: IDentalData[],
): Map<number, IDentalData[]> => {
  const byTooth = new Map<number, IDentalData[]>()
  for (const entry of dentalData) {
    const toothNumber = parseToothNumber(entry.tooth_number)
    if (toothNumber < 1 || toothNumber > 32) {
      continue
    }
    const list = byTooth.get(toothNumber) ?? []
    list.push(entry)
    byTooth.set(toothNumber, list)
  }
  return byTooth
}

const getToothBridgeRoles = (
  entries: IDentalData[],
): { hasPontic: boolean; hasAbutment: boolean } => {
  let hasPontic = false
  let hasAbutment = false

  for (const entry of entries) {
    if (entry.condition === 'abutment') {
      hasAbutment = true
      continue
    }

    if (entry.condition !== 'bridge') {
      continue
    }

    if (entry.type?.endsWith('_abutment')) {
      hasAbutment = true
      continue
    }

    if (!entry.type || entry.type.endsWith('_bridge')) {
      hasPontic = true
    }
  }

  return { hasPontic, hasAbutment }
}

const getExplicitConnector = (
  entries: IDentalData[],
  role: 'bridge' | 'abutment',
): BridgeConnectorType | null => {
  for (const entry of entries) {
    if (entry.condition !== role) {
      continue
    }
    if (isConnectorType(entry.type)) {
      return entry.type
    }
  }
  return null
}

const resolveConnectorForRole = (
  toothNumber: number,
  role: 'bridge' | 'abutment',
  byTooth: Map<number, IDentalData[]>,
): BridgeConnectorType | null => {
  const entries = byTooth.get(toothNumber) ?? []
  const explicit = getExplicitConnector(entries, role)
  if (explicit) {
    return explicit
  }

  const hasRole =
    role === 'abutment'
      ? entries.some((e) => e.condition === 'abutment')
      : entries.some(
          (e) =>
            e.condition === 'bridge' && (!e.type || e.type.endsWith('_bridge')),
        )

  if (!hasRole) {
    return null
  }

  const { mesial, distal } = getMesialDistalNeighbors(toothNumber)
  const mesialNeighbor =
    mesial !== null ? getToothBridgeRoles(byTooth.get(mesial) ?? []) : null
  const distalNeighbor =
    distal !== null ? getToothBridgeRoles(byTooth.get(distal) ?? []) : null

  let showMesial = false
  let showDistal = false

  if (role === 'abutment') {
    showMesial = mesialNeighbor?.hasPontic ?? false
    showDistal = distalNeighbor?.hasPontic ?? false
  } else {
    showMesial = Boolean(
      mesialNeighbor?.hasAbutment || mesialNeighbor?.hasPontic,
    )
    showDistal = Boolean(
      distalNeighbor?.hasAbutment || distalNeighbor?.hasPontic,
    )
  }

  const suffix = role === 'abutment' ? 'abutment' : 'bridge'

  if (showMesial && showDistal) {
    return `right_left_${suffix}` as BridgeConnectorType
  }
  if (showMesial) {
    return `left_${suffix}` as BridgeConnectorType
  }
  if (showDistal) {
    return `right_${suffix}` as BridgeConnectorType
  }

  return null
}

const inferredConnectorCache = new WeakMap<IDentalData, IDentalData>()

const withInferredConnector = (
  entry: IDentalData,
  connector: BridgeConnectorType,
): IDentalData => {
  if (entry.type === connector) {
    return entry
  }
  const cached = inferredConnectorCache.get(entry)
  if (cached?.type === connector) {
    return cached
  }
  const resolved = { ...entry, type: connector }
  inferredConnectorCache.set(entry, resolved)
  return resolved
}

/**
 * Infer mesial/distal connector types for bridge and abutment rows that lack them,
 * based on pontic/abutment roles on anatomical neighbor teeth.
 * The same source row keeps the same inferred object across calls.
 */
export const resolveBridgeConnectorsInDentalData = (
  dentalData: IDentalData[],
): IDentalData[] => {
  let hasBridgeRole = false
  for (let index = 0; index < dentalData.length; index += 1) {
    const condition = dentalData[index]?.condition
    if (condition === 'bridge' || condition === 'abutment') {
      hasBridgeRole = true
      break
    }
  }
  if (!hasBridgeRole) {
    return dentalData
  }

  const byTooth = groupDentalDataByTooth(dentalData)
  const connectorByToothRole = new Map<string, BridgeConnectorType>()

  for (let toothNumber = 1; toothNumber <= 32; toothNumber += 1) {
    const abutmentConnector = resolveConnectorForRole(
      toothNumber,
      'abutment',
      byTooth,
    )
    if (abutmentConnector) {
      connectorByToothRole.set(`${toothNumber}:abutment`, abutmentConnector)
    }

    const bridgeConnector = resolveConnectorForRole(
      toothNumber,
      'bridge',
      byTooth,
    )
    if (bridgeConnector) {
      connectorByToothRole.set(`${toothNumber}:bridge`, bridgeConnector)
    }
  }

  let changed = false
  const resolved = dentalData.map((entry) => {
    const toothNumber = parseToothNumber(entry.tooth_number)
    if (toothNumber < 1) {
      return entry
    }

    if (isConnectorType(entry.type)) {
      return entry
    }

    if (entry.condition === 'abutment') {
      const connector = connectorByToothRole.get(`${toothNumber}:abutment`)
      if (!connector) {
        return entry
      }
      const next = withInferredConnector(entry, connector)
      if (next !== entry) {
        changed = true
      }
      return next
    }

    if (
      entry.condition === 'bridge' &&
      (!entry.type || !entry.type.endsWith('_abutment'))
    ) {
      const connector = connectorByToothRole.get(`${toothNumber}:bridge`)
      if (!connector) {
        return entry
      }
      const next = withInferredConnector(entry, connector)
      if (next !== entry) {
        changed = true
      }
      return next
    }

    return entry
  })

  return changed ? resolved : dentalData
}
